// The deposit paragraph in a pre (welcome) email — one definition, shared by
// the draft builder that writes it and the Outbox toggle that takes it away.
// Same shape and the same reasoning as lib/waivers.js: the toggle can only
// cleanly remove text it can recognise, so exactly one place is allowed to
// decide what that text is.
//
// Pure module — no browser or node deps. The deposit URL is passed IN rather
// than built here, because the builder and the card derive an origin
// differently and neither of them should be this file's problem.

// The default when a booking carries no explicit amount. Mirrors the builder's
// long-standing fallback; kept here so the rebuilt block matches the original
// to the character.
export const DEFAULT_DEPOSIT = 100;

export const DEPOSIT_LEAD_PREFIX = 'To lock in your appointment, please send a $';
export const DEPOSIT_TAIL =
  'Deposits are non-refundable and protect against no-shows; the full amount is credited to your final invoice. Without a deposit on file we may need to release the time if our schedule fills up.';

export function depositAmountFor(amount) {
  const n = Number(amount);
  return n > 0 ? n : DEFAULT_DEPOSIT;
}

// The exact lines the pre-email carries. Returned as an array so the builder can
// splice them into its own `lines` without re-deciding the blank lines.
export function depositLines(amount, url) {
  return [
    '',
    DEPOSIT_LEAD_PREFIX + depositAmountFor(amount).toFixed(2) + ' deposit:',
    '',
    String(url || ''),
    '',
    DEPOSIT_TAIL,
  ];
}

export function hasDepositBlock(body) {
  const t = String(body || '');
  return t.includes(DEPOSIT_LEAD_PREFIX) && t.includes(DEPOSIT_TAIL);
}

// Remove the block: from the "To lock in your appointment…" line through the end
// of the non-refundable sentence, collapsing the blank line it leaves behind.
//
// 🚨 Anchored on BOTH ends and bails unless both are present, in that order. A
// body the operator has reworded is left completely alone rather than
// half-deleted — the failure mode of a greedy match here is an email that asks
// for money with the explanation stripped out of it.
export function withoutDepositBlock(body) {
  const t = String(body || '');
  if (!hasDepositBlock(t)) return t;
  const start = t.indexOf(DEPOSIT_LEAD_PREFIX);
  const tailAt = t.indexOf(DEPOSIT_TAIL, start);
  if (tailAt < 0) return t;
  const end = tailAt + DEPOSIT_TAIL.length;
  const out = t.slice(0, start) + t.slice(end);
  return out.replace(/\n{3,}/g, '\n\n').trimEnd();
}

// Put it back, immediately above the customer-portal paragraph where the
// builder emits it. If that anchor is gone (heavily reworded body), it goes in
// above the sign-off; failing that, at the end.
const PORTAL_LEAD = 'You can follow your detail in real time';
const SIGNOFF = '— 3C Detailing';
export function withDepositBlock(body, amount, url) {
  const t = String(body || '');
  if (hasDepositBlock(t)) return t;
  const block = depositLines(amount, url).join('\n').replace(/^\n/, '');
  let at = t.indexOf(PORTAL_LEAD);
  if (at < 0) at = t.lastIndexOf(SIGNOFF);
  if (at < 0) return t.trimEnd() + '\n\n' + block;
  return t.slice(0, at).trimEnd() + '\n\n' + block + '\n\n' + t.slice(at);
}
